import { FeatureFlag } from '@ps/web-core-enums';
import type { TrackedTranslation } from '@ps/web-shared-components';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import CoachMark from '@swf/components/CoachMark/CoachMark';
import StarsButton from '@swf/components/StarsButton/StarsButton';
import useFeature from '@swf/core/hooks/useFeature';
import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import {
  GameTileSection,
  NoData,
  RightSideBar,
  useCheckUserClubRole,
  useHomeGamesWidget,
  useMapGameData
} from '@swf/poker';

import { PKR_USERS_IN_LEADER_BOARD_NUMBER } from '../../../constants/constants';
import { HGOnboarding, TileType, UserClubRole } from '../../../types/common';
import NewClub from '../../CoachMark/CreateGame/NewClub/NewClub';
import NoDataIcon from '../../Tournaments/assets/no-data.svg';
import Announcements from '../Announcements/Announcements';
import type { Props } from '../Container';
import { options as createGameOptions } from '../Header/CreateGameButtons/CreateGameButtons';

import LastTournamentResult from './LastTournamentResult/LastTournamentResult';
import {
  TXTCLI_Cash_Games,
  TXTCLI_Recent_Club_Tournaments,
  TXTCLI_Registered_In_Tournaments,
  TXTCLI_Scheduled_Tournaments,
  TXTWCL_No_Games_to_Show,
  TXTWCL_The_games_will_be_shown_here_a_ELL
} from './Lobby.messages.json';
import styles from './Lobby.scss';

const Lobby: React.FC<Props> = (props) => {
  const { widgetSelect, widgetDispatch, widgetId } = useHomeGamesWidget();
  const { clubId } = useParams<{ clubId: string }>();
  const { modal } = useDispatch<Dispatch>();
  const isManager = useCheckUserClubRole(clubId, [UserClubRole.Manager]);
  const isAdmin = useCheckUserClubRole(clubId, [UserClubRole.Admin]);
  const isCashGamesActive = useSelector(widgetSelect.isCashGameActive);
  const isTournsActive = useSelector(widgetSelect.isTournsActive);

  const isMobile = useSelector(select.device.isMobile);

  const createGameOBEnabled = useFeature(FeatureFlag.PKRHomeGamesOnboardingCreateGame);
  const isRightSideEnabled = useFeature(FeatureFlag.PKRHomeGamesEmptyStateSideWall);

  const createdClub = useSelector((rootState: RootState) =>
    widgetSelect.localStorageItem(rootState, `onboarding[${HGOnboarding.CreatedClub}]`)
  );
  const onboardingCompleted = useSelector((rootState: RootState) =>
    widgetSelect.localStorageItem(rootState, `onboarding[${HGOnboarding.NewClubCreateGame}]`)
  );
  const showCoachMark = isManager && createGameOBEnabled && createdClub && !onboardingCompleted;

  const { mappedTables, mappedTournaments, mappedRecentTournaments } = useMapGameData({
    shouldMapTables: true,
    shouldMapTournaments: true
  });
  const scheduledTournamentTiles = useSelector((rootState: RootState) =>
    widgetSelect.getScheduledTournTiles(rootState, mappedTournaments)
  );
  const registeredTournamentTiles = useSelector((rootState: RootState) =>
    widgetSelect.getRegisteredTournTiles(rootState, mappedTournaments)
  );

  const showRegistered = !isEmpty(registeredTournamentTiles[clubId]);
  const showScheduled = !isEmpty(scheduledTournamentTiles[clubId]);
  const showTables = !isEmpty(mappedTables[clubId]);
  const showUnavailable = !isEmpty(mappedRecentTournaments[clubId]);

  const handleCreateClick = useCallback(
    (path: string) => {
      modal.enqueue({
        path,
        locationState: { clubId: Number(clubId), widgetId, widgetDispatch, widgetSelect }
      });
    },
    [clubId, modal, widgetDispatch, widgetId, widgetSelect]
  );

  const handleCoachMarkDismiss = useCallback(
    () =>
      widgetDispatch.setLocalStorageItem({
        onboarding: { [HGOnboarding.NewClubCreateGame]: true }
      }),
    [widgetDispatch]
  );

  useEffect(() => {
    widgetDispatch.fetchClubSeasonRanks({
      clubId,
      limit: PKR_USERS_IN_LEADER_BOARD_NUMBER
    });

    return () => {
      widgetDispatch.clearClubSeasonRanks();
    };
  }, [clubId, widgetDispatch]);

  const actionButtons = React.useMemo(
    () => (
      <div className={styles.noDataButtons}>
        {createGameOptions(isCashGamesActive, isTournsActive).map(({ data, value, isActive }) => (
          <StarsButton
            key={`${value}`}
            disabled={!isActive}
            onClick={() => {
              handleCreateClick(`${value}`);

              if (showCoachMark) {
                handleCoachMarkDismiss();
              }
            }}
            translation={data as TrackedTranslation}
          />
        ))}
      </div>
    ),
    [isCashGamesActive, isTournsActive, handleCoachMarkDismiss, handleCreateClick, showCoachMark]
  );

  return (
    <div className={classNames(styles.container)}>
      <div
        className={classNames(styles.centerContainer, {
          [styles.noRightSide]: !isRightSideEnabled
        })}
      >
        <Announcements />
        <LastTournamentResult />
        {showRegistered && (
          <GameTileSection
            title={<Translation {...TXTCLI_Registered_In_Tournaments} />}
            games={registeredTournamentTiles[clubId]}
            type={TileType.Registered}
            source={clubId}
            loading={props.loading}
          />
        )}
        {showScheduled && (
          <GameTileSection
            title={<Translation {...TXTCLI_Scheduled_Tournaments} />}
            games={scheduledTournamentTiles[clubId]}
            type={TileType.Upcoming}
            source={clubId}
            loading={props.loading}
          />
        )}
        {showTables && (
          <GameTileSection
            title={<Translation {...TXTCLI_Cash_Games} />}
            games={mappedTables[clubId]}
            type={TileType.Cash}
            source={clubId}
            loading={props.loading}
          />
        )}
        {showUnavailable && (
          <GameTileSection
            games={mappedRecentTournaments[clubId]}
            title={<Translation {...TXTCLI_Recent_Club_Tournaments} />}
            type={TileType.Recent}
            source={clubId}
            loading={props.loading}
          />
        )}
        {!showRegistered && !showScheduled && !showTables && !showUnavailable && (
          <NoData
            className={classNames(styles.noData, { [styles.noDataMobile]: isMobile })}
            icon={<NoDataIcon />}
            primaryText={<Translation {...TXTWCL_No_Games_to_Show} />}
            secondaryText={<Translation {...TXTWCL_The_games_will_be_shown_here_a_ELL} />}
          >
            {showCoachMark ? (
              <CoachMark
                content={<NewClub onDismiss={handleCoachMarkDismiss} />}
                placement="left-start"
              >
                {actionButtons}
              </CoachMark>
            ) : (
              (isManager || isAdmin) && [actionButtons]
            )}
          </NoData>
        )}
      </div>
      {isRightSideEnabled && <RightSideBar />}
    </div>
  );
};

export default Lobby;
